// ResponseSetModal — a required set name + an editable Responses list
// (color · name · value rows). One component for both jobs: given a `set` it
// edits that one (PATCH /form-choices/update/:id), without one it creates
// (POST /form-choices/create). The form, its validation and its payload build
// are identical either way, so only the seed, the endpoint and the copy differ.
//
// It edits from the row the list already holds — no GET /form-choices/:id — since
// the list response carries `name` and `choices`, which is all this form needs.
//
// Opens stacked over FormResponsesModal; on success the list refetches (cache
// invalidation).

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Button, Field, Icon, IconButton, Input, Modal } from '@/components/common';
import {
  useCreateFormChoiceMutation,
  useUpdateFormChoiceMutation,
  type ResponseSet,
  type ResponseSetChoice,
} from '@/api/endpoints/formChoicesApi';

interface ResponseSetModalProps {
  open: boolean;
  onClose: () => void;
  /** Present → edit that set (PATCH); absent → create a new one (POST). */
  set?: ResponseSet | null;
}

// New rows default to the design's green swatch (fully opaque).
const DEFAULT_COLOR = '#38cb89ff';
const emptyRow = (): ResponseSetChoice => ({ label: '', value: '', color: DEFAULT_COLOR });

// #rrggbb <-> the stored 8-digit hex (picking a color makes it fully opaque).
const toRgb = (color: string) => {
  const h = color?.startsWith('#') ? color.slice(1) : '';
  return h.length >= 6 ? `#${h.slice(0, 6)}` : '#38cb89';
};

const ResponseSetModal = ({ open, onClose, set = null }: ResponseSetModalProps) => {
  const { t } = useTranslation();
  const [createFormChoice, { isLoading: creating }] = useCreateFormChoiceMutation();
  const [updateFormChoice, { isLoading: updating }] = useUpdateFormChoiceMutation();
  const isLoading = creating || updating;
  const editing = Boolean(set);
  const [name, setName] = useState('');
  const [rows, setRows] = useState<ResponseSetChoice[]>([emptyRow()]);
  const [submitted, setSubmitted] = useState(false);

  // Depends on `set` too, so reopening for a different row reseeds rather than
  // keeping the previous one's values. A set with no choices still gets one
  // blank row, since the editor always shows at least one.
  useEffect(() => {
    if (open) {
      setName(set?.name ?? '');
      setRows(set?.choices.length ? set.choices : [emptyRow()]);
      setSubmitted(false);
    }
  }, [open, set]);

  const nameMissing = !name.trim();
  const labeledRows = rows.filter((r) => r.label.trim());
  const noResponses = labeledRows.length === 0;

  const patchRow = (i: number, part: Partial<ResponseSetChoice>) =>
    setRows((prev) => prev.map((r, x) => (x === i ? { ...r, ...part } : r)));

  const submit = async () => {
    setSubmitted(true);
    if (nameMissing || noResponses) return;
    const payload = {
      title: name.trim(),
      choices: labeledRows.map((r) => ({
        label: r.label.trim(),
        value: r.value.trim(),
        color: r.color,
      })),
    };
    try {
      if (set) await updateFormChoice({ id: set.id, ...payload }).unwrap();
      else await createFormChoice(payload).unwrap();
      toast.success(t(editing ? 'forms_responses_updated' : 'forms_responses_created'));
      onClose();
    } catch {
      toast.error(t('error_generic'));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      className='add-response-set'
      icon='list-checks'
      title={t(editing ? 'forms_responses_edit_title' : 'forms_responses_add_title')}
      subtitle={t('forms_responses_add_subtitle')}
      footer={
        <>
          <Button variant='secondary' onClick={onClose} disabled={isLoading}>
            {t('button_cancel')}
          </Button>
          <Button iconStart={editing ? 'check' : 'plus'} onClick={submit} loading={isLoading}>
            {t(editing ? 'button_save_changes' : 'forms_responses_add_submit')}
          </Button>
        </>
      }
    >
      <Field
        label={t('forms_responses_name_label')}
        required
        error={submitted && nameMissing ? t('forms_responses_name_required') : undefined}
      >
        <Input
          autoFocus
          value={name}
          placeholder={t('forms_responses_name_ph')}
          error={submitted && nameMissing}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>

      <div className='add-response-set__responses'>
        <div className='add-response-set__responses-head'>
          <span className='add-response-set__responses-title'>
            {t('forms_responses_add_responses')}
          </span>
          <span className='add-response-set__responses-note'>{t('forms_responses_add_note')}</span>
        </div>

        <div className='add-response-set__cols' aria-hidden='true'>
          <span>{t('forms_responses_col_col')}</span>
          <span>{t('forms_responses_col_name')}</span>
          <span>{t('forms_responses_col_value')}</span>
          <span />
        </div>

        {rows.map((row, i) => (
          // Index keys are fine — rows only append/remove in place.
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} className='add-response-set__row'>
            <label className='add-response-set__swatch' style={{ background: toRgb(row.color) }}>
              <input
                type='color'
                value={toRgb(row.color)}
                aria-label={t('forms_responses_col_col')}
                onChange={(e) => patchRow(i, { color: `${e.target.value}ff` })}
              />
            </label>
            <Input
              size='sm'
              value={row.label}
              placeholder={t('forms_responses_name_row_ph')}
              aria-label={t('forms_responses_col_name')}
              onChange={(e) => patchRow(i, { label: e.target.value })}
            />
            <Input
              size='sm'
              className='add-response-set__value'
              inputMode='numeric'
              value={row.value}
              placeholder='—'
              aria-label={t('forms_responses_col_value')}
              onChange={(e) => patchRow(i, { value: e.target.value })}
            />
            <IconButton
              icon='trash-2'
              variant='ghost'
              size='sm'
              className='add-response-set__delete'
              aria-label={t('forms_responses_delete_row')}
              disabled={rows.length === 1}
              onClick={() => setRows((prev) => prev.filter((_, x) => x !== i))}
            />
          </div>
        ))}

        {submitted && noResponses && (
          <span className='add-response-set__error' role='alert'>
            <Icon name='circle-alert' size={15} aria-hidden='true' />
            {t('forms_responses_min_one')}
          </span>
        )}

        <button
          type='button'
          className='add-response-set__add'
          onClick={() => setRows((prev) => [...prev, emptyRow()])}
        >
          <Icon name='plus' size={15} />
          {t('forms_responses_add_row')}
        </button>
      </div>
    </Modal>
  );
};

export default ResponseSetModal;
